/* eslint-disable import/no-unresolved */
import React from "react";
import { Icon, LoadingSpinner } from "components";
import "./Button.scss";
import classNames from "classnames";

export interface ButtonInterface {
  className?: string;
  colorScheme?: "primary" | "secondary" | "secondaryalt" | "tertiary";
  /** Variants in which a button can be displayed
   * Solid = background colour
   * Outline = border
   * Ghost = no background colour but hover background
   * Link = underline on hover
   */
  variant?: "solid" | "outline" | "ghost" | "link";
  actualButton?: boolean;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
  isDisabled?: React.ButtonHTMLAttributes<HTMLButtonElement>["disabled"];
  isLoading?: boolean;
  loadingText?: string;
  leftIcon?: typeof Icon["name"] | false;
  rightIcon?: typeof Icon["name"] | false;
  onClick?: () => void;
}

/**
 * Returns the stylised button
 *
 * @param className - optional, if an additional class is required
 * @param colorScheme - optional, Uses any of the colour main colour variables
 * @param variant - optional, Sets the styles of the button
 * @param actualButton - optional, if the button is a real button or a span
 * @param type - option, button type
 * @param isDisabled - optional, Renders a disabled button and prevents onClick
 * @param isLoading - optional, Renders a disabled button with a loading spinner
 * @param loadingText - optional, The label to show in the button when `isLoading` is true. If no text is passed, it only shows the spinner
 * @param leftIcon - optional, Icon to display to the left of the text
 * @param rightIcon - optional, Icon to display to the right of the text
 * @param onClick - optional, Callback function to be called when user clicks on button
 * @returns Returns a stylised button
 *
 */
export const Button: React.FC<ButtonInterface> = ({
  className,
  variant = "solid",
  actualButton = false,
  type = "submit",
  colorScheme = "primary",
  isDisabled,
  onClick,
  ...contentProps
}) => {
  return actualButton ? (
    <button
      className={classNames({
        [`${className}`]: className,
        button: true,
        [`button--${variant}`]: variant,
        [`button--${colorScheme}`]: colorScheme,
      })}
      type={type}
      onClick={onClick}
    >
      <ButtonContent {...contentProps} />
    </button>
  ) : (
    <span
      className={classNames({
        [`${className}`]: className,
        button: true,
        [`button--${variant}`]: variant,
        [`button--${colorScheme}`]: colorScheme,
      })}
      onClick={onClick}
    >
      <ButtonContent {...contentProps} />
    </span>
  );
};

const ButtonContent = ({ leftIcon, isLoading, loadingText, rightIcon, children }): JSX.Element<ButtonInterface> => {
  return (
    <>
      {/* If isLoading display the spinner
        Loading spinner: Will display the same colour as the text
       */}

      {leftIcon && !isLoading && <Icon name={leftIcon} className="left-icon" />}

      {isLoading && <LoadingSpinner size="button" />}

      {/* if isLoading and loadingText display those. Else display the default children */}
      {isLoading ? loadingText && <span>{loadingText}</span> : children}

      {rightIcon && !isLoading && <Icon name={rightIcon} className="right-icon" />}
    </>
  );
};
